import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Textarea } from '@/components/forms/textarea'
import { Drawer } from '@/components/overlays/drawer'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'

import type { OAuthResource, OidcClientKind, OidcClientPayload, OidcClientScope } from './api'

const emptyValues: {
  clientKind: OidcClientKind
  defaultScopeIds: string[]
  name: string
  redirectUris: string
  resourceId: string
  status: OidcClientPayload['status']
  tokenEndpointAuthMethod: OidcClientPayload['token_endpoint_auth_method']
} = {
  clientKind: 'oidc_embed',
  defaultScopeIds: [],
  name: '',
  redirectUris: '',
  resourceId: '',
  status: 'active',
  tokenEndpointAuthMethod: 'client_secret_basic',
}

function splitLines(value: string) {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
}

function FieldHelp({ text }: { text: string }) {
  return (
    <HelpTooltip label={text} widthClassName="w-[280px]">
      {text}
    </HelpTooltip>
  )
}

export function OidcClientFormDrawer({
  clientScopes,
  isSaving,
  onOpenChange,
  onSave,
  open,
  resources,
}: {
  clientScopes: OidcClientScope[]
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSave: (payload: OidcClientPayload) => void
  open: boolean
  resources: OAuthResource[]
}) {
  const { t } = useTranslation('adminIdp')
  const [values, setValues] = useState(emptyValues)
  const redirectUris = splitLines(values.redirectUris)
  const isMcpOAuth = values.clientKind === 'mcp_oauth'
  const activeResources = resources.filter((resource) => resource.status === 'active')
  const enabledClientScopes = clientScopes.filter((scope) => scope.enabled)

  return (
    <Drawer
      footer={
        <div className="flex justify-end gap-3">
          <Button onClick={() => onOpenChange(false)} variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={
              !values.name.trim() ||
              (isMcpOAuth && (!values.resourceId || values.defaultScopeIds.length === 0))
            }
            isLoading={isSaving}
            onClick={() =>
              onSave({
                application_ids: [],
                client_kind: values.clientKind,
                default_client_scope_ids: isMcpOAuth ? values.defaultScopeIds : [],
                name: values.name.trim(),
                optional_client_scope_ids: [],
                redirect_uris: redirectUris,
                resource_id: isMcpOAuth ? values.resourceId : null,
                status: values.status,
                token_endpoint_auth_method: values.tokenEndpointAuthMethod,
              })
            }
          >
            {t('actions.create')}
          </Button>
        </div>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={t('clients.create')}
    >
      <div className="grid gap-6">
        <div className="rounded-xl border border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] px-4 py-3 text-sm leading-6 text-[var(--color-text-muted)]">
          {isMcpOAuth ? t('clients.formHints.mcp') : t('clients.formHints.oidc')}
        </div>
        <FormField
          htmlFor="oidc-client-name"
          label={t('clients.fields.name')}
          labelExtra={<FieldHelp text={t('clients.fields.nameHelp')} />}
          required
        >
          <Input
            autoFocus
            id="oidc-client-name"
            onChange={(event) => setValues({ ...values, name: event.target.value })}
            placeholder={t('clients.placeholders.name')}
            value={values.name}
          />
        </FormField>
        <FormField
          htmlFor="oidc-client-kind"
          label={t('clients.fields.clientKind')}
          labelExtra={<FieldHelp text={t('clients.fields.clientKindHelp')} />}
        >
          <Select
            id="oidc-client-kind"
            onValueChange={(value) => {
              const clientKind = value as OidcClientKind
              setValues({
                ...values,
                clientKind,
                defaultScopeIds: clientKind === 'mcp_oauth' ? values.defaultScopeIds : [],
                resourceId:
                  clientKind === 'mcp_oauth'
                    ? values.resourceId || activeResources[0]?.id || ''
                    : '',
              })
            }}
            options={[
              { label: 'OpenID Connect', value: 'oidc_embed' },
              { label: 'MCP OAuth', value: 'mcp_oauth' },
            ]}
            value={values.clientKind}
          />
        </FormField>
        {isMcpOAuth ? (
          <>
            <FormField
              error={
                activeResources.length === 0 ? t('clients.errors.noActiveResources') : undefined
              }
              hint={t('clients.fields.resourceHint')}
              htmlFor="oidc-client-resource"
              label={t('clients.fields.resource')}
              required
            >
              <Select
                disabled={activeResources.length === 0}
                id="oidc-client-resource"
                onValueChange={(resourceId) => setValues({ ...values, resourceId })}
                options={activeResources.map((resource) => ({
                  description: resource.resource_uri,
                  label: resource.name,
                  value: resource.id,
                }))}
                value={values.resourceId}
              />
            </FormField>
            <FormField
              error={
                enabledClientScopes.length === 0 ? t('clients.errors.noEnabledScopes') : undefined
              }
              label={t('clients.fields.defaultScopes')}
              required
            >
              <div className="overflow-hidden rounded-xl border border-[var(--color-panel-border)]">
                {enabledClientScopes.map((scope) => (
                  <label
                    className="flex cursor-pointer items-center gap-3 border-b border-[var(--color-divider)] px-3 py-2.5 last:border-b-0"
                    key={scope.id}
                  >
                    <input
                      checked={values.defaultScopeIds.includes(scope.id)}
                      className="size-4 accent-[var(--color-primary)]"
                      onChange={(event) =>
                        setValues({
                          ...values,
                          defaultScopeIds: event.target.checked
                            ? [...values.defaultScopeIds, scope.id]
                            : values.defaultScopeIds.filter((id) => id !== scope.id),
                        })
                      }
                      type="checkbox"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block">
                        <span className="font-medium">{scope.display_name}</span>
                        <code className="ml-2 text-xs text-[var(--color-text-muted)]">
                          {scope.name}
                        </code>
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
                        {scope.description || t('clients.fields.scopeFallbackDescription')}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </FormField>
          </>
        ) : null}
        <FormField
          htmlFor="oidc-client-callback-urls"
          label={t('clients.fields.callbackUrls')}
          labelExtra={<FieldHelp text={t('clients.fields.callbackUrlsHelp')} />}
        >
          <Textarea
            id="oidc-client-callback-urls"
            onChange={(event) => setValues({ ...values, redirectUris: event.target.value })}
            placeholder={t('clients.placeholders.callbackUrls')}
            rows={4}
            value={values.redirectUris}
          />
        </FormField>
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField
            htmlFor="oidc-client-auth-method"
            label={t('clients.fields.authMethod')}
            labelExtra={<FieldHelp text={t('clients.fields.authMethodHelp')} />}
          >
            <Select
              id="oidc-client-auth-method"
              onValueChange={(value) =>
                setValues({
                  ...values,
                  tokenEndpointAuthMethod: value as OidcClientPayload['token_endpoint_auth_method'],
                })
              }
              options={[
                { label: 'client_secret_basic', value: 'client_secret_basic' },
                { label: 'client_secret_post', value: 'client_secret_post' },
              ]}
              value={values.tokenEndpointAuthMethod}
            />
          </FormField>
          <FormField
            htmlFor="oidc-client-status"
            label={t('clients.fields.status')}
            labelExtra={<FieldHelp text={t('clients.fields.statusHelp')} />}
          >
            <Select
              id="oidc-client-status"
              onValueChange={(value) =>
                setValues({ ...values, status: value as OidcClientPayload['status'] })
              }
              options={[
                { label: t('status.enabled'), value: 'active' },
                { label: t('status.disabled'), value: 'inactive' },
              ]}
              value={values.status}
            />
          </FormField>
        </div>
      </div>
    </Drawer>
  )
}
